import { onDetach } from 'utils/DomUtils';
import { useLayoutEffect } from 'react';
import { Root } from 'react-dom/client';

export type RenderCallback = (root?: Root) => void;

export interface IRenderComponentProps {
  container: HTMLElement;
  component: JSX.Element;
  callback?: RenderCallback;
  /**
   * Automatically unmounts component when container is removed
   */
  autoUnmount: boolean;
}

const roots = new Map<HTMLElement, Root>();

export function unMount(container: HTMLElement, unmountContainer = false) {
  if (roots.has(container)) {
    try {
      const root = roots.get(container)!;
      root.unmount();
      unmountContainer && container.remove();
    } catch (_e) {
      // sometimes external code will try to clean up something more than once
      // because those are difficult to chase down, it's safer to catch this.
    } finally {
      roots.delete(container);
    }
  }
}

function WrapperComponent({ component, callback }: Pick<IRenderComponentProps, 'callback' | 'component'>): JSX.Element {
  // Approach is suggested in the discussion https://github.com/reactwg/react-18/discussions/5#discussioncomment-2276079
  useLayoutEffect(() => {
    callback && callback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return component;
}

export async function renderComponent({ container, component, callback, autoUnmount }: IRenderComponentProps) {
  const { createRoot } = await import('react-dom/client');

  const isUpdate = roots.has(container);
  const root = isUpdate ? roots.get(container)! : createRoot(container);

  if (!isUpdate) {
    roots.set(container, root);
  }
  if (!isUpdate && autoUnmount) {
    onDetach(container, () => unMount(container));
  }

  try {
    root.render(<WrapperComponent component={component} callback={() => callback && callback(root)} />);
  } catch (e) {
    if (DEV_BUILD || DEV_SERVER) {
      console.dir(e);
      console.dir(container);
      console.trace();
    }
  }

  return {
    unmount: (unmountContainer = false) => unMount(container, unmountContainer),
  };
}
